import { REALTIME_PATH, type ClientFrame, type ServerFrame } from '@kairelo/shared';
import { log } from './logging.js';

/**
 * WebSocket client.
 *
 * The session credential is sent in the first frame, never in the URL, so it
 * cannot land in a proxy log or browser history (concept §5).
 *
 * Reconnection uses exponential backoff with jitter and resumes from the last
 * sequence number seen, so a dropped connection during the active period
 * recovers without duplicating messages (concept §4).
 */

export type RealtimeStatus = 'connecting' | 'open' | 'closed' | 'expired' | 'rejected';

export interface RealtimeOptions {
  scope: 'owner-inbox' | 'conversation';
  /**
   * Visitor session credential. Omitted for the owner, whose session is an
   * HttpOnly cookie the browser attaches to the upgrade request — keeping it
   * out of JavaScript entirely (concept §5).
   */
  token?: string;
  conversationId?: string;
  onFrame: (frame: ServerFrame) => void;
  onStatus: (status: RealtimeStatus, detail?: string) => void;
}

/**
 * Outbound frames held while the socket is not yet usable.
 *
 * Capped so a long outage cannot grow without bound; the sender is told when a
 * frame is refused rather than having it silently dropped.
 */
const MAX_QUEUED_FRAMES = 50;

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private attempts = 0;
  private closedByUs = false;
  private lastSeq = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * An open socket is not a usable one.
   *
   * The server authenticates the first frame, so anything sent between `open`
   * and `ready` is rejected as unauthenticated — and the server closes the
   * socket for it. That window is invisible on a fast machine and easily
   * hundreds of milliseconds on a loaded one, which is long enough for someone
   * to hit send. Frames are therefore queued until `ready` arrives and flushed
   * in order, which also means a message written during a brief reconnect is
   * delivered rather than lost.
   */
  private ready = false;
  private outbox: ClientFrame[] = [];

  constructor(private readonly options: RealtimeOptions) {}

  connect(): void {
    this.closedByUs = false;
    this.open();
  }

  private endpoint(): string {
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${location.host}${REALTIME_PATH}`;
  }

  private open(): void {
    this.options.onStatus('connecting');
    const socket = new WebSocket(this.endpoint());
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.ready = false;
      const hello: ClientFrame = {
        t: 'hello',
        scope: this.options.scope,
        ...(this.options.token ? { token: this.options.token } : {}),
        ...(this.options.conversationId ? { conversationId: this.options.conversationId } : {}),
        ...(this.lastSeq > 0 ? { sinceSeq: this.lastSeq } : {}),
      };
      socket.send(JSON.stringify(hello));

      this.heartbeatTimer = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ t: 'ping' }));
      }, 20_000);
    };

    socket.onmessage = (event) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(String(event.data)) as ServerFrame;
      } catch {
        log.warn('discarded malformed realtime frame');
        return;
      }

      if (frame.t === 'message') this.lastSeq = Math.max(this.lastSeq, frame.seq);
      if (frame.t === 'ready') {
        this.ready = true;
        this.flush();
        this.options.onStatus('open');
      }

      if (frame.t === 'expired') {
        this.closedByUs = true;
        this.options.onStatus('expired');
      }
      if (frame.t === 'error' && (frame.code === 'forbidden' || frame.code === 'unauthenticated')) {
        this.closedByUs = true;
        this.options.onStatus('rejected', frame.message);
      }

      this.options.onFrame(frame);
    };

    socket.onclose = (event) => {
      this.ready = false;
      this.clearHeartbeat();
      if (this.closedByUs) {
        this.options.onStatus(event.code === 4008 ? 'expired' : 'closed');
        return;
      }
      // 4008 = conversation ended; 4001/4003 = credential rejected. None of
      // these are worth retrying.
      if (event.code === 4008) {
        this.options.onStatus('expired');
        return;
      }
      if (event.code === 4001 || event.code === 4003 || event.code === 4009) {
        this.options.onStatus('rejected', event.reason || undefined);
        return;
      }
      this.options.onStatus('closed');
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // The close handler runs next and owns the retry decision.
    };
  }

  private scheduleReconnect(): void {
    if (this.closedByUs) return;
    this.attempts += 1;
    const base = Math.min(1000 * 2 ** (this.attempts - 1), 15_000);
    const delay = base + Math.random() * 500;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /** Flushes anything queued while the socket was not usable. */
  private flush(): void {
    if (!this.ready || !this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const pending = this.outbox;
    this.outbox = [];
    for (const frame of pending) this.socket.send(JSON.stringify(frame));
  }

  /**
   * Queues or sends a frame. Returns false only when it was actually refused,
   * so a caller can tell the user their message did not go.
   */
  send(frame: ClientFrame): boolean {
    if (this.ready && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(frame));
      return true;
    }
    if (this.closedByUs) return false;
    // Transient signals are worthless once late; only content is worth holding.
    if (frame.t === 'typing' || frame.t === 'ping' || frame.t === 'translation-state') {
      return false;
    }
    if (this.outbox.length >= MAX_QUEUED_FRAMES) return false;
    this.outbox.push(frame);
    return true;
  }

  close(): void {
    this.closedByUs = true;
    this.ready = false;
    this.outbox = [];
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.clearHeartbeat();
    this.socket?.close(1000, 'client closed');
    this.socket = null;
  }
}
